import { useLayoutEffect, useRef } from 'react';

const MQ = '(max-width: 767px)';

/**
 * Auto-advances horizontal scroll on small screens; pauses on interaction, resumes after resumeAfterMs.
 * Content must be duplicated (two identical halves) so scrollWidth / 2 is one loop.
 * @param {object} opts
 * @param {1 | -1} [opts.direction] 1 = forward scroll (content moves left), -1 = backward (opposite marquee)
 * @param {number} [opts.speed]
 * @param {number} [opts.resumeAfterMs]
 */
export function useMobileMarqueeLoop({ direction = 1, speed = 0.45, resumeAfterMs = 2400 }) {
  const viewportRef = useRef(null);
  const pausedRef = useRef(false);
  const resumeTimerRef = useRef(null);

  useLayoutEffect(() => {
    const mq = window.matchMedia(MQ);
    let raf = 0;
    let boundEl = null;

    const clearResume = () => {
      if (resumeTimerRef.current) {
        clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = null;
      }
    };

    const onInteractStart = () => {
      pausedRef.current = true;
      clearResume();
    };

    const onInteractEnd = () => {
      clearResume();
      resumeTimerRef.current = setTimeout(() => {
        pausedRef.current = false;
        resumeTimerRef.current = null;
      }, resumeAfterMs);
    };

    const unbind = () => {
      if (!boundEl) return;
      boundEl.removeEventListener('touchstart', onInteractStart);
      boundEl.removeEventListener('touchend', onInteractEnd);
      boundEl.removeEventListener('touchcancel', onInteractEnd);
      boundEl.removeEventListener('pointerdown', onInteractStart);
      boundEl.removeEventListener('pointerup', onInteractEnd);
      boundEl.removeEventListener('pointerleave', onInteractEnd);
      boundEl.removeEventListener('wheel', onInteractStart);
      boundEl = null;
    };

    const bind = (el) => {
      if (!el || boundEl === el) return;
      unbind();
      boundEl = el;
      el.addEventListener('touchstart', onInteractStart, { passive: true });
      el.addEventListener('touchend', onInteractEnd, { passive: true });
      el.addEventListener('touchcancel', onInteractEnd, { passive: true });
      el.addEventListener('pointerdown', onInteractStart);
      el.addEventListener('pointerup', onInteractEnd);
      el.addEventListener('pointerleave', onInteractEnd);
      el.addEventListener('wheel', onInteractStart, { passive: true });
    };

    const tick = () => {
      const el = viewportRef.current;
      if (mq.matches && el) {
        bind(el);
        if (
          !pausedRef.current &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
          const half = el.scrollWidth / 2;
          if (half > el.clientWidth + 1) {
            if (direction > 0) {
              let next = el.scrollLeft + speed;
              if (next >= half - 0.5) next -= half;
              el.scrollLeft = next;
            } else {
              let next = el.scrollLeft - speed;
              if (next < 0) next += half;
              el.scrollLeft = next;
            }
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };

    const stopLoop = () => {
      cancelAnimationFrame(raf);
      clearResume();
      unbind();
    };

    if (mq.matches) {
      raf = requestAnimationFrame(tick);
    }

    const onMq = () => {
      stopLoop();
      if (mq.matches) {
        raf = requestAnimationFrame(tick);
      }
    };

    mq.addEventListener('change', onMq);
    return () => {
      mq.removeEventListener('change', onMq);
      stopLoop();
    };
  }, [direction, speed, resumeAfterMs]);

  return viewportRef;
}
